<template>
  <div class="app">
    <!-- 拖拽上传覆盖层 -->
    <DragDropOverlay :is-dragging="isDragOver" />

    <!-- Toast 通知容器 -->
    <ToastContainer />

    <!-- 登录提示模态框 -->
    <LoginPromptModal />

    <!-- 用户设置模态框 -->
    <UserSettingsModal />

    <!-- 桌面端导航 -->
    <AppNavigation :items="items" />

    <!-- 移动端导航 -->
    <MobileNavigation />

    <!-- 主内容区域 -->
    <main class="main-content">
      <!-- 下拉刷新指示器 -->
      <PullRefreshIndicator
        :pull-distance="pullDistance"
        :is-pulling="isPulling"
        :is-refreshing="isRefreshing"
      />

      <!-- 路由视图 -->
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- 快捷键面板 -->
    <ShortcutsPanel :is-open="showShortcutsPanel" @close="closeShortcutsPanel" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useItemsStore } from './stores/items';
import { useUserStore } from './stores/user';
import { useToastStore } from './stores/toast';
import { useHistoryStore } from './stores/history';
import { usePullRefresh } from './composables/usePullRefresh';
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts';
import { useDragDrop } from './composables/useDragDrop';
import DragDropOverlay from './components/DragDropOverlay.vue';
import ToastContainer from './components/ToastContainer.vue';
import PullRefreshIndicator from './components/PullRefreshIndicator.vue';
import AppNavigation from './components/AppNavigation.vue';
import MobileNavigation from './components/MobileNavigation.vue';
import ShortcutsPanel from './components/ShortcutsPanel.vue';
import LoginPromptModal from './components/LoginPromptModal.vue';
import UserSettingsModal from './components/UserSettingsModal.vue';

const itemsStore = useItemsStore();
const userStore = useUserStore();
const toast = useToastStore();
const history = useHistoryStore();
const { items, pendingCount } = storeToRefs(itemsStore);
const { fetchItems, createItem, undo, redo } = itemsStore;
const { canUndo, canRedo } = storeToRefs(history);

// 快捷键面板
const showShortcutsPanel = ref(false);

function toggleShortcutsPanel() {
  showShortcutsPanel.value = !showShortcutsPanel.value;
}

function closeShortcutsPanel() {
  showShortcutsPanel.value = false;
}

// 下拉刷新
const { isRefreshing, pullDistance, isPulling } = usePullRefresh({
  threshold: 80,
  onRefresh: async () => {
    await fetchItems();
    toast.success('刷新成功');
  },
});

// 快捷键支持
useKeyboardShortcuts({
  onUndo: () => {
    if (canUndo.value) {
      const success = undo();
      if (success) {
        toast.success('已撤销');
      }
    } else {
      toast.info('没有可撤销的操作');
    }
  },
  onRedo: () => {
    if (canRedo.value) {
      const success = redo();
      if (success) {
        toast.success('已重做');
      }
    } else {
      toast.info('没有可重做的操作');
    }
  },
  onHelp: () => {
    toggleShortcutsPanel();
  },
});

// 拖拽上传支持
const { isDragging: isDragOver } = useDragDrop({
  onText: (text) => {
    createItem({ content: text });
    toast.success('内容已添加');
  },
  onUrl: (url) => {
    createItem({ content: url, url: url });
    toast.success('链接已添加');
  },
  onFile: async (file) => {
    if (file.type.startsWith('image/')) {
      try {
        const base64 = await fileToBase64(file);
        createItem({
          content: file.name,
          url: base64,
          source: '拖拽上传',
        });
        toast.success('图片已添加');
      } catch {
        toast.error('图片处理失败');
      }
    } else {
      try {
        const text = await file.text();
        createItem({ content: text });
        toast.success('文件内容已添加');
      } catch {
        toast.error('文件读取失败');
      }
    }
  },
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

onMounted(async () => {
  // 初始化用户认证状态
  await userStore.initialize();

  // 只有认证成功后才加载数据
  if (userStore.isAuthenticated) {
    fetchItems();
  }

  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }

  watch(pendingCount, (newCount, oldCount) => {
    if (newCount > oldCount && oldCount === 0) {
      showNotification();
    }
  });
});

function showNotification() {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('SnapClip', {
      body: `您有 ${pendingCount.value} 条内容待处理`,
      icon: '/favicon.svg',
    });
  }
}
</script>

<style>
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
  width: 100%;
}

#app {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
}

.app {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 240px; /* 桌面端侧边栏宽度 */
  min-height: 100vh;
  background: var(--bg-primary);
}

/* 路由过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 暗夜模式 */
:global(.dark) .main-content {
  background: var(--bg-primary);
  color: var(--text-primary);
}

:global(.dark) .app {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-bottom: 60px; /* 为底部导航留出空间 */
  }
}
</style>
